/**
 * RecreateApiKeyInstructionalPanel — 340px persistent push panel for access_denied credentials.
 *
 * Opened from the blocked credential row in CredentialPickerExistingBlock when
 * blockedReason.reason === 'access_denied'. The panel slides in alongside the
 * creation shell (push, NOT overlay) so the user can alt-tab to CampMinder
 * while the instructions remain visible.
 *
 * Layout contract: this component renders inside a flex row parent at the
 * DashboardCreationShell level. It narrows the creation shell via flex
 * compression without a modal overlay.
 *
 * On "Save & validate" success:
 *   1. Close panel.
 *   2. Re-fetch availableCredentials → row converts to normal selectable pill.
 *   3. Announce via role="alert".
 *
 * Validation failure messages:
 *   - access_denied (scope still missing): "This key is still missing required scopes.
 *     Make sure you selected all available scopes when creating the key."
 *   - auth_failed (wrong/old key pasted): "This key is not valid. Check that you copied the correct key."
 *   - generic: "Failed to save. Please try again."
 *
 * Brand guide §37g (2026-05-26). Phase E Slice 2.4 — TDD §11.
 */

import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { X, ExternalLink, Loader2, Check } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { CredentialsAPI } from '@/API/Credentials'
import { prefetchAvailableCredentialsForApp } from '@/services/DashboardChatService'
import type { AppDispatch, RootState } from '@/redux'

// ─── Step data ────────────────────────────────────────────────────────────────

interface RecreateStep {
  title: string
  description: string
  link?: string
  linkLabel?: string
}

const RECREATE_API_KEY_STEPS: RecreateStep[] = [
  {
    title: 'Open CampMinder Admin',
    description: 'Go to your CampMinder administration portal.',
    link: 'https://admin.campminder.com',
    linkLabel: 'Open CampMinder Admin',
  },
  {
    title: 'Navigate to API Keys',
    description: 'In the admin portal, go to Settings → Integrations → API Keys.',
  },
  {
    title: 'Create a new API key',
    description: 'Click New API Key. Give it a descriptive name (e.g. "The Camp Stack Integration").',
  },
  {
    title: 'Enable all required scopes',
    description: 'Select all available scopes when prompted. Do not leave any scope unchecked.',
  },
  {
    title: 'Copy the new API key',
    description: 'CampMinder will show your new API key once. Copy it — it will not be shown again.',
  },
  {
    title: 'Paste it below and validate',
    description: 'Return here, paste the new key in the field below, and click "Save & validate."',
  },
]

// ─── Validation error helpers ─────────────────────────────────────────────────

type ValidationError = 'auth_failed' | 'access_denied' | 'generic'

function validationErrorMessage(error: ValidationError): string {
  switch (error) {
    case 'access_denied':
      return 'This key is still missing required scopes. Make sure you selected all available scopes when creating the key.'
    case 'auth_failed':
      return 'This key is not valid. Check that you copied the correct key.'
    case 'generic':
      return 'Failed to save. Please try again.'
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

interface RecreateApiKeyInstructionalPanelProps {
  credentialId: string
  credentialLabel: string
  /** Source app code (e.g. 'campminder') for post-success prefetch. */
  sourceAppCode: string
  /** Close the panel without saving. */
  onClose: () => void
  /** Fired when the update succeeds; caller surfaces success announcement. */
  onSuccess: (credentialLabel: string) => void
}

export function RecreateApiKeyInstructionalPanel({
  credentialId,
  credentialLabel,
  sourceAppCode,
  onClose,
  onSuccess,
}: RecreateApiKeyInstructionalPanelProps) {
  const dispatch: AppDispatch = useDispatch()
  const token = useSelector((s: RootState) => s.user.token)

  const [newApiKey, setNewApiKey] = useState('')
  const [isValidating, setIsValidating] = useState(false)
  const [validationError, setValidationError] = useState<ValidationError | null>(null)

  const handleSaveAndValidate = async () => {
    if (!token || !newApiKey.trim()) return
    setIsValidating(true)
    setValidationError(null)

    try {
      // Step 1: fetch credential details for loginMethodId + nickname.
      // Use the API module directly (not the credentialsService thunk) to avoid
      // toggling the global app.loading flag, which would block the entire creation
      // UI with a full-screen overlay while this local validate flow is in progress.
      const detailsResp = await CredentialsAPI.getCredentialDetails(token, credentialId)
      if (!detailsResp.data) {
        setValidationError('generic')
        setIsValidating(false)
        return
      }

      const { loginMethodId, nickname } = detailsResp.data

      // Step 2: PUT updated credential with the new API key.
      const updateResp = await CredentialsAPI.update(token, credentialId, {
        appId: detailsResp.data.app.id,
        loginMethodId,
        nickname,
        credentialValues: [
          { credentialAttributeCode: 'api_key', attributeValue: newApiKey.trim() },
        ],
      })

      if (!updateResp.data) {
        const errMsg = updateResp.err ?? ''
        if (errMsg.toLowerCase().includes('access_denied') || errMsg.toLowerCase().includes('scope')) {
          setValidationError('access_denied')
        } else if (errMsg.toLowerCase().includes('auth_failed') || errMsg.toLowerCase().includes('invalid')) {
          setValidationError('auth_failed')
        } else {
          setValidationError('generic')
        }
        setIsValidating(false)
        return
      }

      // Step 3: Refetch availableCredentials — BE probe already ran.
      await dispatch(prefetchAvailableCredentialsForApp(token, sourceAppCode))

      setIsValidating(false)
      onClose()
      onSuccess(credentialLabel)
    } catch {
      setValidationError('generic')
      setIsValidating(false)
    }
  }

  return (
    <aside
      className="w-[340px] flex-shrink-0 bg-card border-l border-border flex flex-col h-screen overflow-hidden animate-[slideIn_250ms_ease-out]"
      role="complementary"
      aria-label="Instructions: Re-create CampMinder API key with all scopes"
    >
      {/* Header */}
      <div className="flex items-start justify-between px-5 py-4 flex-shrink-0 border-b border-border">
        <div>
          <p className="text-sm font-bold text-primary leading-snug">
            Re-create API key with all scopes
          </p>
          <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
            Follow these steps in CampMinder, then paste your new API key below.
          </p>
        </div>
        <button
          type="button"
          className="w-7 h-7 flex items-center justify-center rounded-md text-muted-foreground hover:bg-muted transition-colors flex-shrink-0 focus-visible:ring-2 focus-visible:ring-focusRing focus-visible:ring-offset-2"
          aria-label="Close instructions panel"
          onClick={onClose}
        >
          <X size={16} aria-hidden="true" />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-5 py-4">
        {/* Context box */}
        <div className="bg-accent-background border border-accent/15 rounded-lg px-3 py-2.5 mb-4">
          <p className="text-[13px] text-secondary leading-relaxed">
            <strong>Why?</strong> Your CampMinder subscription already gives access to all APIs.
            The existing key was created without enabling all required scopes.
            Creating a new key with all scopes selected will fix this.
          </p>
        </div>

        {/* Steps */}
        <ol className="flex flex-col gap-3">
          {RECREATE_API_KEY_STEPS.map((step, i) => (
            <li key={i} className="flex gap-2.5">
              <div className="w-[22px] h-[22px] rounded-full bg-primary text-white text-[11px] font-bold flex items-center justify-center flex-shrink-0 mt-px">
                {i + 1}
              </div>
              <div>
                <p className="text-[13px] font-semibold text-primary mb-0.5">{step.title}</p>
                <p className="text-xs text-secondary leading-snug">{step.description}</p>
                {step.link && (
                  <a
                    href={step.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs font-semibold text-accent flex items-center gap-1 mt-1 hover:underline focus-visible:ring-2 focus-visible:ring-focusRing focus-visible:ring-offset-2 rounded"
                  >
                    {step.linkLabel}
                    <ExternalLink size={10} aria-hidden="true" />
                  </a>
                )}
              </div>
            </li>
          ))}
        </ol>

        <Separator className="my-4" />

        {/* Paste field */}
        <p className="text-xs font-semibold text-secondary mb-1.5">New API Key</p>
        <p className="text-xs text-muted-foreground mb-2 leading-relaxed">
          Paste your newly-created CampMinder API key here. It will be validated immediately.
        </p>
        <Input
          type="password"
          placeholder="Paste your new API key here"
          aria-label="New CampMinder API key — paste after completing the steps above"
          value={newApiKey}
          onChange={e => {
            setNewApiKey(e.target.value)
            if (validationError) setValidationError(null)
          }}
          autoComplete="off"
        />
        {validationError && (
          <p
            className="text-xs text-destructive mt-1.5"
            role="alert"
            aria-live="polite"
          >
            {validationErrorMessage(validationError)}
          </p>
        )}
      </div>

      {/* Footer */}
      <div className="px-5 pb-5 pt-3 flex-shrink-0 border-t border-border">
        <Button
          type="button"
          className="w-full font-semibold"
          onClick={() => void handleSaveAndValidate()}
          disabled={!newApiKey.trim() || isValidating}
          aria-busy={isValidating}
        >
          {isValidating
            ? (
              <>
                <Loader2 size={16} className="animate-spin mr-2" aria-hidden="true" />
                Validating&hellip;
              </>
            )
            : (
              <>
                <Check size={14} className="mr-2" aria-hidden="true" />
                Save &amp; validate
              </>
            )
          }
        </Button>
      </div>
    </aside>
  )
}
